import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";

const PlayerContext = createContext(null);

export const usePlayer = () => useContext(PlayerContext);

export const PlayerProvider = ({ children }) => {
  const audioRef = useRef(new Audio());
  const [current, setCurrent] = useState(null);
  const [playlist, setPlaylist] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(0.8);

  // Ad state (self-served audio pre-roll)
  const [ad, setAd] = useState(null);
  const [adCountdown, setAdCountdown] = useState(0);
  const adRef = useRef(null);
  const pendingRef = useRef(null);
  const countdownTimer = useRef(null);

  const actuallyPlay = (track) => {
    adRef.current = null;
    setAd(null);
    audioRef.current.src = `${API}${track.stream_url || `/stream/${track.id}`}`;
    audioRef.current.play();
    setCurrent(track);
    setIsPlaying(true);
    api.post(`/tracks/${track.id}/play`).catch(() => {});
  };

  const finishAd = () => {
    if (countdownTimer.current) clearInterval(countdownTimer.current);
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending) actuallyPlay(pending);
  };

  useEffect(() => {
    const audio = audioRef.current;
    audio.volume = volume;
    const onTime = () => setProgress(audio.currentTime);
    const onMeta = () => setDuration(audio.duration || 0);
    const onEnd = () => {
      if (adRef.current) finishAd();
      else next();
    };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended", onEnd);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended", onEnd);
    };
    // eslint-disable-next-line
  }, [current, playlist, ad]);

  const startAd = async (track) => {
    let adData = null;
    try {
      const { data } = await api.get("/ads/preroll");
      adData = data;
    } catch (_) {}
    if (adData && adData.id) {
      adRef.current = adData;
      pendingRef.current = track;
      setAd(adData);
      setAdCountdown(adData.skip_after || 5);
      api.post(`/ads/${adData.id}/impression`).catch(() => {});
      audioRef.current.src = `${API}${adData.media_url}`;
      audioRef.current.play();
      setIsPlaying(true);
      if (countdownTimer.current) clearInterval(countdownTimer.current);
      countdownTimer.current = setInterval(() => {
        setAdCountdown((c) => {
          if (c <= 1) { clearInterval(countdownTimer.current); return 0; }
          return c - 1;
        });
      }, 1000);
    } else {
      actuallyPlay(track);
    }
  };

  const playTrack = (track, list = null) => {
    if (list) setPlaylist(list);
    if (adRef.current) return; // ad active, ignore
    if (current?.id === track.id) {
      togglePlay();
      return;
    }
    startPlayback(track);
  };

  // Play a self-served audio pre-roll (if one is active) before the track.
  const startPlayback = (track) => {
    startAd(track);
  };

  const skipAd = () => {
    if (adCountdown > 0) return;
    finishAd();
  };

  const clickAd = () => {
    if (!ad) return;
    api.post(`/ads/${ad.id}/click`).catch(() => {});
    if (ad.link_url) window.open(ad.link_url, "_blank", "noopener");
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (audio.paused) { audio.play(); setIsPlaying(true); }
    else { audio.pause(); setIsPlaying(false); }
  };

  const seek = (t) => {
    if (adRef.current) return;
    audioRef.current.currentTime = t;
    setProgress(t);
  };

  const setVolume = (v) => {
    audioRef.current.volume = v;
    setVolumeState(v);
  };

  const next = () => {
    if (!current || playlist.length === 0) return;
    const idx = playlist.findIndex((t) => t.id === current.id);
    const nextTrack = playlist[(idx + 1) % playlist.length];
    if (nextTrack) startPlayback(nextTrack);
  };

  const prev = () => {
    if (!current || playlist.length === 0) return;
    const idx = playlist.findIndex((t) => t.id === current.id);
    const prevTrack = playlist[(idx - 1 + playlist.length) % playlist.length];
    if (prevTrack) startPlayback(prevTrack);
  };

  return (
    <PlayerContext.Provider
      value={{
        current, isPlaying, progress, duration, volume,
        ad, adCountdown, skipAd, clickAd,
        playTrack, togglePlay, seek, setVolume, next, prev,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
};
